import React, { useRef } from 'react';
import { useInView } from 'framer-motion';
import { motion } from '../../lib/motionShim';
import PhoneSwap from '../PhoneSwap';
import GhostNumeral from '../GhostNumeral';
import Chips from '../Chips';

/** 05 · PROVE — strength as a health marker, not a scoreboard. */
const StrengthSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-120px' });

  const rise = (delay) => ({
    initial: { opacity: 0, y: 26 },
    animate: isInView ? { opacity: 1, y: 0 } : {},
    transition: { duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] },
  });

  return (
    <section
      id="strength"
      ref={ref}
      className="relative py-24 md:py-32 bg-ston-bg-alt overflow-hidden"
      data-testid="strength-section"
    >
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[560px] h-[560px] rounded-full" style={{ background: 'radial-gradient(closest-side, rgba(147,51,234,0.08), rgba(147,51,234,0.040) 55%, transparent 78%)' }} />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24 items-center">

          {/* Copy */}
          <div>
            <motion.span className="section-label inline-block" {...rise(0)}>
              05 · PROVE
            </motion.span>
            <motion.h2
              className="mt-4 font-outfit font-extrabold italic uppercase leading-[1.02]"
              style={{ fontSize: 'clamp(30px, 4vw, 52px)', letterSpacing: '-0.025em' }}
              {...rise(0.1)}
            >
              <span className="block text-ston-text">Muscle isn't vanity.</span>
              <span className="block gradient-text">It's insurance.</span>
            </motion.h2>
            <motion.p
              className="mt-6 text-[17px] text-ston-text-secondary leading-relaxed max-w-[52ch]"
              {...rise(0.2)}
            >
              Strength and lean mass track with how well you age — which is why
              STON.FIT treats every logged set as data, not a scoreboard. A PR wall,
              estimated 1RM curves per lift, muscle balance across the week, and a
              twelve-week consistency map that won't let you lie to yourself.
            </motion.p>
            <Chips items={['PR wall', 'Est. 1RM trend', 'Muscle balance', '12-week map']} />
          </div>

          {/* Devices — overview front, consistency map behind */}
          <motion.div
            className="relative flex justify-center lg:justify-end"
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="relative" style={{ perspective: 1500 }}>
              <GhostNumeral
                className="absolute -top-16 -left-10 md:-left-20"
                stroke="rgba(168,85,247,0.16)"
                size="clamp(120px, 13vw, 200px)"
              >
                220
              </GhostNumeral>

              <PhoneSwap
                side="right"
                width={296}
                front={{
                  src: '/screens/lift-overview.webp',
                  alt: 'Lift analytics — 220 kg leg press PR, weekly volume and streaks',
                  glowColor: 'rgba(147,51,234,0.25)',
                }}
                back={{
                  src: '/screens/lift-consistency.webp',
                  alt: 'Twelve-week training consistency heatmap and muscle balance chart',
                }}
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default StrengthSection;
